import { useEffect, useCallback } from 'react';
import { X, Copy, Loader2, Play } from 'lucide-react';
import { useToastStore } from '@/stores/toast-store';

interface ReviewChangesModalProps {
  statements: string[];
  onExecute: () => void;
  onCopySQL: () => void;
  onCancel: () => void;
  loading: boolean;
}

/**
 * Applies minimal syntax-like coloring to a SQL token.
 * Keywords are highlighted with accent-blue, strings with accent-green,
 * and NULL/TRUE/FALSE with accent-orange.
 */
function colorizeSQL(sql: string): React.ReactNode[] {
  const keywords = new Set([
    'BEGIN', 'COMMIT', 'UPDATE', 'SET', 'WHERE', 'INSERT', 'INTO',
    'VALUES', 'DELETE', 'FROM', 'AND', 'OR', 'NOT', 'IN', 'IS',
    'SELECT', 'CREATE', 'ALTER', 'DROP', 'TABLE', 'INDEX',
  ]);
  const parts: React.ReactNode[] = [];
  // Split preserving tokens: quoted strings, identifiers, words, punctuation
  const regex = /('(?:''|[^'])*')|("(?:""|[^"])*")|(\b[A-Z_]+\b)|([^'"A-Za-z_]+)|(\b[a-z_]\w*\b)/gi;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = regex.exec(sql)) !== null) {
    const token = match[0];
    if (match[1]) {
      // String literal
      parts.push(<span key={key++} className="text-accent-green">{token}</span>);
    } else if (match[2]) {
      // Quoted identifier
      parts.push(<span key={key++} className="text-accent-purple">{token}</span>);
    } else if (match[3]) {
      const upper = token.toUpperCase();
      if (upper === 'NULL' || upper === 'TRUE' || upper === 'FALSE') {
        parts.push(<span key={key++} className="text-accent-orange">{token}</span>);
      } else if (keywords.has(upper)) {
        parts.push(<span key={key++} className="text-accent-blue font-semibold">{token}</span>);
      } else {
        parts.push(<span key={key++}>{token}</span>);
      }
    } else {
      parts.push(<span key={key++}>{token}</span>);
    }
  }
  return parts;
}

export function ReviewChangesModal({
  statements,
  onExecute,
  onCopySQL,
  onCancel,
  loading,
}: ReviewChangesModalProps) {
  const addToast = useToastStore((s) => s.addToast);

  const fullScript = `BEGIN;\n\n${statements.map((s) => `${s};`).join('\n\n')}\n\nCOMMIT;`;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onCancel();
    },
    [onCancel, loading],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(fullScript).then(() => {
      addToast('success', 'SQL script copied to clipboard');
      onCopySQL();
    }).catch(() => {
      addToast('error', 'Failed to copy to clipboard');
    });
  }, [fullScript, addToast, onCopySQL]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        className="w-full max-w-[600px] flex flex-col bg-dbgo-modal border rounded-lg shadow-2xl animate-fade-in max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b flex-shrink-0">
          <div className="flex items-center gap-2">
            <Play size={16} className="text-accent-green" />
            <h2 className="text-sm font-semibold text-text-primary">Review Changes</h2>
            <span className="text-xs text-text-muted">
              {statements.length} change{statements.length !== 1 ? 's' : ''} to apply
            </span>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="w-6 h-6 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors disabled:opacity-40"
          >
            <X size={14} />
          </button>
        </div>

        {/* SQL Script */}
        <div className="flex-1 min-h-0 overflow-auto px-5 py-4">
          <pre className="bg-dbgo-input border rounded-sm p-4 text-[12px] leading-relaxed font-mono text-text-primary overflow-x-auto whitespace-pre-wrap break-words">
            <div className="text-accent-blue font-semibold">BEGIN;</div>
            <div className="mt-2" />
            {statements.map((stmt, i) => (
              <div key={i} className="mb-2">
                {colorizeSQL(stmt)}<span className="text-text-muted">;</span>
              </div>
            ))}
            <div className="text-accent-blue font-semibold">COMMIT;</div>
          </pre>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t flex-shrink-0">
          <button
            type="button"
            onClick={handleCopy}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-[6px] text-xs font-medium text-text-secondary hover:text-text-primary rounded-sm hover:bg-dbgo-tab-hover transition-colors disabled:opacity-40"
          >
            <Copy size={12} />
            Copy SQL
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-3 py-[6px] text-xs font-medium text-text-secondary hover:text-text-primary rounded-sm hover:bg-dbgo-tab-hover transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onExecute}
              disabled={loading}
              className="flex items-center gap-1.5 px-4 py-[6px] text-xs font-medium text-white rounded-sm bg-accent-green hover:bg-accent-green/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading && <Loader2 size={12} className="animate-spin" />}
              {loading ? 'Applying...' : 'Apply Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
